import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { ConversationItem, ConversationListResponse } from '@chatapp-inbox/shared';
import { getConversation } from '../../api';
import { conversationKey } from '../conversation';

/** Por quanto tempo uma conversa buscada só para saber o responsável vale sem nova busca. */
const FRESH_MS = 60_000;

/**
 * Quem é o responsável pela conversa, segundo o que já está em cache (a conversa aberta ou as
 * listas): `undefined` = não sei; `null` = sem responsável; senão o id.
 */
export function lookupAssignee(
  queryClient: QueryClient,
  conversationId: string,
): string | null | undefined {
  const open = queryClient.getQueryData<ConversationItem>(conversationKey(conversationId));
  if (open) return open.assignedTo?.id ?? null;
  const lists = queryClient.getQueriesData<InfiniteData<ConversationListResponse, string | null>>({
    queryKey: ['conversations'],
  });
  for (const [, data] of lists) {
    for (const page of data?.pages ?? []) {
      const found = page.conversations.find((c) => c.id === conversationId);
      if (found) return found.assignedTo?.id ?? null;
    }
  }
  return undefined;
}

/**
 * Traz a conversa para o cache só para descobrir o responsável. Serve a mensagem nova de uma
 * conversa que esta tela nunca carregou: sem isso, toda mensagem de colega tocaria o som de quem
 * só abriu a aba "Minhas". Falha de rede não impede nada (quem chama trata como "não sei").
 */
export async function resolveAssignee(
  queryClient: QueryClient,
  conversationId: string,
): Promise<void> {
  await queryClient.fetchQuery({
    queryKey: conversationKey(conversationId),
    queryFn: ({ signal }) => getConversation(conversationId, signal),
    staleTime: FRESH_MS,
  });
}
